import { Link } from 'react-router-dom';
import { ArrowRight, Check, Headset, Layers, Lock, Sparkles } from 'lucide-react';
import { PRODUCTS_CONFIG } from '../constants';
import { useLanguage } from '../hooks/useLanguage';
import { trackEvent } from '../utils/analytics';
import MockupMedicare from '../components/products/MockupMedicare';
import MockupTienda360 from '../components/products/MockupTienda360';
import '../styles/components/Products.css';

const MOCKUP_BY_ID = {
  medicare: MockupMedicare,
  tienda360: MockupTienda360,
};

const BENEFIT_ICONS = {
  support: Headset,
  scalable: Layers,
  secure: Lock,
  custom: Sparkles,
};

const BENEFIT_KEYS = ['support', 'scalable', 'secure', 'custom'];

const Products = () => {
  const { t } = useLanguage();
  const overview = t.products.overview;

  return (
    <main className='products-page'>
      <section className='products-hero'>
        <div className='products-hero-bg' />
        <div className='products-hero-overlay' />
        <div className='container products-hero-content'>
          <span className='products-eyebrow'>{overview.eyebrow}</span>
          <h1 className='products-hero-title'>{overview.title}</h1>
          <p className='products-hero-subtitle'>{overview.subtitle}</p>
        </div>
      </section>

      <section className='products-grid-section section'>
        <div className='container'>
          <div className='products-grid'>
            {PRODUCTS_CONFIG.map(product => {
              const Mockup = MOCKUP_BY_ID[product.id];
              const productTexts = t.products[product.id];
              const teaser = productTexts.teaser;
              const CategoryIcon = product.categoryIcon;

              // Productos con sitio externo (ej. Tienda360) abren en pestaña nueva;
              // el resto navega a su página de detalle interna.
              const ctaProps = product.externalUrl
                ? {
                    component: 'a',
                    linkProps: {
                      href: product.externalUrl,
                      target: '_blank',
                      rel: 'noopener noreferrer',
                    },
                  }
                : {
                    component: Link,
                    linkProps: { to: product.route },
                  };
              const CtaLink = ctaProps.component;

              return (
                <article
                  key={product.id}
                  className={`product-card product-card--${product.accent}`}
                >
                  <div className='product-card-mockup'>
                    {Mockup && <Mockup />}
                  </div>

                  <div className='product-card-body'>
                    <div className='product-card-category'>
                      <CategoryIcon size={14} />
                      <span>{teaser.category}</span>
                    </div>

                    <h2 className='product-card-title'>{teaser.title}</h2>
                    <p className='product-card-subtitle'>{teaser.subtitle}</p>
                    <p className='product-card-description'>
                      {teaser.description}
                    </p>

                    <ul className='product-card-features'>
                      {product.features.map(feature => {
                        const FeatureIcon = feature.icon;
                        const featureTexts =
                          productTexts.features[feature.id];
                        return (
                          <li key={feature.id}>
                            <FeatureIcon size={16} />
                            <span>{featureTexts.title}</span>
                          </li>
                        );
                      })}
                    </ul>

                    <div className='product-card-actions'>
                      <CtaLink
                        {...ctaProps.linkProps}
                        className='product-cta product-cta--primary'
                        onClick={() =>
                          trackEvent('cta_click', {
                            cta: 'product_trial',
                            product: product.id,
                            location: 'products_overview',
                          })
                        }
                      >
                        {overview.ctaTrial}
                        <ArrowRight size={16} />
                      </CtaLink>
                      <CtaLink
                        {...ctaProps.linkProps}
                        className='product-cta product-cta--ghost'
                        onClick={() =>
                          trackEvent('cta_click', {
                            cta: 'product_learn_more',
                            product: product.id,
                            location: 'products_overview',
                          })
                        }
                      >
                        {overview.ctaLearnMore}
                      </CtaLink>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className='products-benefits section'>
        <div className='container'>
          <div className='products-benefits-header'>
            <h2 className='products-benefits-title'>
              {overview.benefitsTitle}
            </h2>
            <p className='products-benefits-subtitle'>
              {overview.benefitsSubtitle}
            </p>
          </div>

          <div className='products-benefits-grid'>
            {BENEFIT_KEYS.map(key => {
              const Icon = BENEFIT_ICONS[key];
              const benefit = overview.benefits[key];
              return (
                <div key={key} className='products-benefit'>
                  <div className='products-benefit-icon'>
                    <Icon size={24} />
                  </div>
                  <h3 className='products-benefit-title'>{benefit.title}</h3>
                  <p className='products-benefit-description'>
                    {benefit.description}
                  </p>
                  <div className='products-benefit-check'>
                    <Check size={14} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Products;
